import { homeForRole } from '../utils/roles';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { consumeSessionEnded } from '../utils/sessionEnded';
import { useT } from '../i18n/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import '../styles/distributor.css';

export const TOO_MANY_ATTEMPTS_MESSAGE =
  'Too many sign-in attempts. Please wait a minute and try again.';

const PORTAL_ACCESS_MESSAGE =
  'Access denied. This portal is for MedEffects staff and clinic accounts.';
const LOCKED_MESSAGE = 'Account is temporarily locked. Please try again later.';

// Select app-authored feedback without rendering server diagnostics or network error text.
export function loginErrorMessage(err, distributor = false) {
  if (err?.response?.status === 429) return TOO_MANY_ATTEMPTS_MESSAGE;
  if (err?.response?.status === 401) {
    const reason = err?.response?.data?.error || err?.response?.data?.message;
    if (typeof reason === 'string' && reason.startsWith('Account is temporarily locked')) {
      return LOCKED_MESSAGE;
    }
    return distributor ? 'Invalid username or password' : 'Invalid email or password';
  }
  if (!err?.response && err?.message === PORTAL_ACCESS_MESSAGE) return PORTAL_ACCESS_MESSAGE;
  if (!err?.response && err?.message === 'Access denied. This portal is for distributor accounts.')
    return err.message;
  return 'Login failed';
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, distributorLogin } = useAuth();
  const navigate = useNavigate();
  const isDistributor = useLocation().pathname === '/distributor/login';
  const t = useT();

  // Arrived here because a session ended (inactivity timer, failed refresh)? Say so, once.
  useEffect(() => {
    setNotice(consumeSessionEnded());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!(isDistributor ? username.trim() : email.trim()) || !password.trim()) {
      setError(
        isDistributor ? 'Username and password are required' : 'Email and password are required'
      );
      return;
    }

    setLoading(true);
    try {
      if (isDistributor) {
        const account = await distributorLogin(username.trim(), password);
        navigate(homeForRole(account), { replace: true });
      } else {
        await login(email, password);
        navigate('/', { replace: true });
      }
    } catch (err) {
      // The typed email stays in its field: the person corrects the password, not the whole form.
      setError(loginErrorMessage(err, isDistributor));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 ${
        isDistributor ? 'me-distributor-login' : 'bg-gradient-to-br from-[#1d293d] to-[#0f172b]'
      }`}
    >
      <div className={isDistributor ? 'me-distributor-login-layout' : 'w-full max-w-md'}>
        {isDistributor && (
          <div className="me-distributor-login-story">
            <div className="me-distributor-login-story-brand">
              <img src="/assets/logo.png" alt="MedEffects" />
              <span aria-hidden="true" />
            </div>
            <div>
              <p className="me-distributor-login-story-kicker">{t('A clearer way to work')}</p>
              <p className="me-distributor-login-story-title">
                {t('Your clinics and product bank, in one place.')}
              </p>
              <p className="me-distributor-login-story-copy">
                {t(
                  'Manage clinics, review your bank, and follow order activity from one workspace.'
                )}
              </p>
            </div>
            <div className="me-distributor-login-story-steps" aria-hidden="true">
              <span>{t('Clinics')}</span>
              <span>{t('Product bank')}</span>
              <span>{t('Orders')}</span>
            </div>
          </div>
        )}
        <div className={isDistributor ? 'me-distributor-login-form-side' : ''}>
          {/* Language: the one control that must be findable BEFORE anyone can read the page. */}
          <div className="flex justify-end mb-2">
            <LanguageToggle tone="dark" />
          </div>

          {/* Logo */}
          <div className="text-center mb-8">
            {!isDistributor && (
              <img src="/assets/logo.png" alt="MedEffects" className="h-[30px] mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-semibold text-white">
              {t(isDistributor ? 'Distributor sign in' : 'MedEffects Portal')}
            </h1>
            <p className="text-[#c4d8da] text-sm mt-1">
              {t(
                isDistributor
                  ? 'Use your distributor username and password.'
                  : 'Sign in to continue'
              )}
            </p>
          </div>

          {/* Login Card */}
          <div
            className={`bg-white rounded-2xl shadow-xl p-8 ${
              isDistributor ? 'me-distributor-login-card' : ''
            }`}
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {notice && !error && (
                <div
                  role="status"
                  className="bg-blue-50 border border-blue-200 text-[#1e3a8a] text-sm rounded-lg px-4 py-3"
                >
                  {t(notice)}
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
                  {t(error)}
                </div>
              )}

              <div>
                <label
                  htmlFor="login-identifier"
                  className="block text-sm font-medium text-[#64748b] mb-1.5"
                >
                  {t(isDistributor ? 'Username' : 'Email')}
                </label>
                <input
                  id="login-identifier"
                  type={isDistributor ? 'text' : 'email'}
                  value={isDistributor ? username : email}
                  onChange={(e) =>
                    isDistributor ? setUsername(e.target.value) : setEmail(e.target.value)
                  }
                  placeholder={t(isDistributor ? 'Enter your username' : 'Enter your email')}
                  autoComplete={isDistributor ? 'username' : 'email'}
                  autoCapitalize="none"
                  spellCheck={false}
                  className={`w-full h-[46px] px-4 border border-[#e2e8f0] rounded-[10px] text-sm text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:border-transparent ${isDistributor ? 'focus:ring-[#0b8b8c]' : 'focus:ring-[#0089ff]'}`}
                />
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="block text-sm font-medium text-[#64748b] mb-1.5"
                >
                  {t('Password')}
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('Enter your password')}
                    autoComplete="current-password"
                    autoCapitalize="none"
                    spellCheck={false}
                    className={`w-full h-[46px] pl-4 pr-20 border border-[#e2e8f0] rounded-[10px] text-sm text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:border-transparent ${isDistributor ? 'focus:ring-[#0b8b8c]' : 'focus:ring-[#0089ff]'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={t(showPassword ? 'Hide password' : 'Show password')}
                    aria-controls="login-password"
                    className={`absolute inset-y-0 right-0 px-3 text-sm font-medium rounded-r-[10px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${isDistributor ? 'text-[#08767b] hover:text-[#075f65] focus-visible:ring-[#0b8b8c]' : 'text-[#0070d4] hover:text-[#005eb3] focus-visible:ring-[#0089ff]'}`}
                  >
                    {showPassword ? t('Hide') : t('Show')}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full h-[46px] text-white text-sm font-semibold rounded-[10px] hover:opacity-90 transition-opacity disabled:opacity-50 ${isDistributor ? 'me-distributor-login-button' : 'bg-gradient-to-r from-[#0089ff] to-[#00d4ff]'}`}
              >
                {loading ? t('Signing in...') : t('Sign In')}
              </button>
            </form>
            <p className="mt-5 text-center text-sm">
              <Link
                className={
                  isDistributor
                    ? 'text-[#08767b] underline hover:text-[#075f65]'
                    : 'text-[#0070d4] underline hover:text-[#005eb3]'
                }
                to={isDistributor ? '/login' : '/distributor/login'}
              >
                {t(isDistributor ? 'MedEffects client sign in' : 'Distributor sign in')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
